import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Divider,
} from "@chakra-ui/react";
import {
  FiInfo,
  FiCheckCircle,
  FiXCircle,
  FiClock,
} from "react-icons/fi";
import { BaseBlockProps, UIBlock } from "./types";
import { useLunaStore } from "state/luna";

// Forward declaration - will be set from index to avoid circular deps
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let BlockRendererRef: any = null;

export const setToolConfirmationBlockRenderer = (
  renderer: typeof BlockRendererRef,
) => {
  BlockRendererRef = renderer;
};

interface ToolConfirmationBlockProps extends BaseBlockProps {
  sessionId?: string;
  executionId?: string;
  status?:
    | "pending_confirmation"
    | "confirmed"
    | "denied"
    | "running"
    | "completed"
    | "failed"
    | "pending_external";
  confirmationMessage?: string;
  confirmedBy?: string;
  confirmedAt?: string;
  /** Other blocks from call_blocks to render as message preview */
  previewBlocks?: UIBlock[];
}

interface ConfirmationContent {
  message?: string;
  description?: string;
  tool_label?: string;
  tool_name?: string;
  execution_id?: string;
  tool_input?: Record<string, unknown>;
}

// Extract structured content from block.content
const parseContent = (content: unknown): ConfirmationContent => {
  if (typeof content === "object" && content !== null) {
    return content as ConfirmationContent;
  }
  return {};
};

// Extract message body from tool_input
const getMessageBody = (toolInput: Record<string, unknown>): string | null => {
  // Common field names for message content
  const messageFields = ["message_body", "message", "body", "content", "text"];
  for (const field of messageFields) {
    if (typeof toolInput[field] === "string") {
      return toolInput[field] as string;
    }
  }
  return null;
};

// Extract recipients from tool_input
const getRecipients = (
  toolInput: Record<string, unknown>,
): string[] | null => {
  // Check for user_email field
  if (typeof toolInput.user_email === "string") {
    return [toolInput.user_email];
  }
  // Check for recipients array
  if (Array.isArray(toolInput.recipients)) {
    return toolInput.recipients.map((r) =>
      typeof r === "string" ? r : r?.email || r?.name || String(r),
    );
  }
  return null;
};

export const ToolConfirmationBlock = ({
  block,
  sessionId,
  executionId,
  status = "pending_confirmation",
  confirmedBy,
  confirmedAt,
  previewBlocks,
}: ToolConfirmationBlockProps) => {
  const confirmTool = useLunaStore((state) => state.confirmTool);
  const denyTool = useLunaStore((state) => state.denyTool);

  const isPending = status === "pending_confirmation";
  const isConfirmed = status === "confirmed" || status === "completed";
  const isDenied = status === "denied";
  const isPendingExternal = status === "pending_external";

  // Parse the content to extract structured data
  const content = parseContent(block.content);
  const toolInput = content.tool_input || {};

  // Extract message and recipients from tool_input
  const messageBody = getMessageBody(toolInput);
  const recipients = getRecipients(toolInput);

  const handleConfirm = async () => {
    if (sessionId && executionId) {
      await confirmTool(sessionId, executionId);
    }
  };

  const handleDeny = async () => {
    if (sessionId && executionId) {
      await denyTool(sessionId, executionId);
    }
  };

  return (
    <Box pl="1px" pr="1px" pb="2px">
      <Box
        borderRadius="container.md"
        bg="bg.container"
        boxShadow="0 0 1px 0 rgba(0, 0, 0, 0.32), 0 0 2px 0 rgba(0, 0, 0, 0.08), 0 1px 3px 0 rgba(0, 0, 0, 0.08)"
      >
      {/* Header */}
      <HStack
        px="lg"
        py="md"
        borderBottom="1px solid"
        borderColor="border.default"
        spacing="sm"
      >
        <Icon
          as={isPendingExternal ? FiClock : FiInfo}
          color={isPendingExternal ? "p.500" : "text.body.tertiary"}
          boxSize={4}
        />
        <Text fontWeight="semibold" color="text.body.primary" fontSize="md">
          {isPendingExternal ? "Waiting for Result" : "Confirmation Required"}
        </Text>
      </HStack>

      {/* Content */}
      <VStack align="stretch" spacing="sm" p="lg">
        {/* Preview blocks from call_blocks (context about what's being confirmed) */}
        {previewBlocks && previewBlocks.length > 0 && BlockRendererRef && (
          <>
            {previewBlocks.map((previewBlock, i) => (
              <BlockRendererRef key={previewBlock.id || i} block={previewBlock} />
            ))}
          </>
        )}

        {/* Message section (for tools with message body in tool_input) */}
        {messageBody && (
          <HStack align="start" spacing="lg" w="full">
            <Text
              fontSize="xs"
              color="text.body.tertiary"
              fontWeight="medium"
              w="60px"
              flexShrink={0}
            >
              Message
            </Text>
            <VStack align="stretch" spacing="sm" flex={1}>
              {/* Message preview box */}
              <Box
                bg="bg.app"
                border="1px solid"
                borderColor="border.default"
                borderRadius="component.md"
                p="sm"
                maxH="130px"
                overflow="hidden"
                position="relative"
              >
                <Text
                  fontSize="sm"
                  color="text.body.primary"
                  whiteSpace="pre-wrap"
                  fontFamily="mono"
                  lineHeight="1.5"
                >
                  {messageBody}
                </Text>
              </Box>
            </VStack>
          </HStack>
        )}

        {/* Divider */}
        {messageBody && recipients && <Divider my="lg" />}

        {/* Recipients section */}
        {recipients && recipients.length > 0 && (
          <HStack align="start" spacing="lg" w="full">
            <Text
              fontSize="xs"
              color="text.body.tertiary"
              fontWeight="medium"
              w="60px"
              flexShrink={0}
            >
              Recipients
            </Text>
            <VStack align="start" spacing="sm" flex={1}>
              {recipients.map((recipient, i) => (
                <HStack key={i} spacing="sm">
                  <Box
                    w="16px"
                    h="16px"
                    borderRadius="full"
                    bg="success.100"
                    border="1px solid"
                    borderColor="success.500"
                  />
                  <Text fontSize="xs" color="text.body.primary" fontWeight="medium">
                    {recipient}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </HStack>
        )}
      </VStack>

      {/* Footer with actions */}
      <HStack
        px="lg"
        py="md"
        borderTop="1px solid"
        borderColor="border.default"
        justify="space-between"
      >
        <Text fontSize="md" color="text.body.primary">
          {isPendingExternal
            ? "Action sent - Waiting for result"
            : content.message || "Do you want to proceed with this action?"}
        </Text>
        {isPending ? (
          <HStack spacing="sm">
            <Button
              size="sm"
              bg="success.500"
              color="white"
              _hover={{ bg: "success.600" }}
              leftIcon={<FiCheckCircle />}
              onClick={handleConfirm}
              borderRadius="component.md"
            >
              Confirm
            </Button>
            <Button
              size="sm"
              variant="outline"
              borderColor="error.500"
              color="error.600"
              _hover={{ bg: "error.50" }}
              leftIcon={<FiXCircle />}
              onClick={handleDeny}
              borderRadius="component.md"
            >
              Deny
            </Button>
          </HStack>
        ) : (
          <HStack>
            {isPendingExternal && (
              <HStack
                px="sm"
                py="2xs"
                borderRadius="component.sm"
                bg="p.50"
              >
                <Icon as={FiClock} color="p.500" boxSize={4} />
                <Text fontSize="xs" color="p.600" fontWeight="medium">
                  Waiting
                </Text>
              </HStack>
            )}
            {isConfirmed && (
              <HStack
                px="sm"
                py="2xs"
                borderRadius="component.sm"
                bg="success.50"
              >
                <Icon as={FiCheckCircle} color="success.500" boxSize={4} />
                <Text fontSize="xs" color="success.600" fontWeight="medium">
                  Confirmed
                </Text>
              </HStack>
            )}
            {isDenied && (
              <HStack
                px="sm"
                py="2xs"
                borderRadius="component.sm"
                bg="error.50"
              >
                <Icon as={FiXCircle} color="error.500" boxSize={4} />
                <Text fontSize="xs" color="error.600" fontWeight="medium">
                  Denied
                </Text>
              </HStack>
            )}
          </HStack>
        )}
      </HStack>
      </Box>
    </Box>
  );
};
